import MerklePath from './MerklePath.js';
import BeefTx from './BeefTx.js';
import { toHex, toArray, verifyNotNull, ReaderUint8Array, WriterUint8Array, toUint8Array } from '../primitives/utils.js';
import { hash256 } from '../primitives/Hash.js';
import { BEEF_V1, BEEF_V2, ATOMIC_BEEF } from './BeefConstants.js';
export { BEEF_V1, BEEF_V2, ATOMIC_BEEF, TX_DATA_FORMAT } from './BeefConstants.js';
function mergeCompatibleBumpLevels(levels, other, expectedLevels, validateCombined) {
    if (other.path.length !== expectedLevels)
        throw new Error('Mismatched roots');
    for (let height = 0; height < expectedLevels; height++) {
        mergeCompatibleBumpLevel(levels[height], other.path[height], validateCombined);
    }
}
function mergeCompatibleBumpLevel(level, otherLeaves, validateCombined) {
    for (const otherLeaf of otherLeaves) {
        const existing = level.get(otherLeaf.offset);
        if (existing == null) {
            level.set(otherLeaf.offset, otherLeaf);
            continue;
        }
        mergeCompatibleBumpLeaf(existing, otherLeaf, validateCombined);
    }
}
function mergeCompatibleBumpLeaf(existing, other, validateCombined) {
    if (validateCombined && (existing.hash !== other.hash || existing.duplicate !== other.duplicate)) {
        throw new Error('Mismatched roots');
    }
    if (other.txid != null)
        existing.txid = true;
}
function indexBumpTxids(bump, index, byTxid) {
    for (const leaf of bump.path[0]) {
        if (typeof leaf.hash === 'string')
            byTxid.set(leaf.hash, index);
    }
}
/*
 * BEEF standard: BRC-62: Background Evaluation Extended Format (BEEF) Transactions
 * https://github.com/bsv-blockchain/BRCs/blob/master/transactions/0062.md
 *
 * BUMP standard: BRC-74: BSV Unified Merkle Path (BUMP) Format
 * https://github.com/bsv-blockchain/BRCs/blob/master/transactions/0074.md
 *
 * BRC-95: Atomic BEEF Transactions
 * https://github.com/bsv-blockchain/BRCs/blob/master/transactions/0095.md
 *
 * The Atomic BEEF format is supported by the binary deserialization static method `fromBinary`.
 *
 * BRC-96: BEEF V2, Txid Only Extension
 * https://github.com/bsv-blockchain/BRCs/blob/master/transactions/0096.md
 *
 * A valid serialized BEEF is the cornerstone of Simplified Payment Validation (SPV)
 * where they are exchanged between two non-trusting parties to establish the
 * validity of a newly constructed bitcoin transaction and its inputs from prior
 * transactions.
 *
 * A `Beef` is fundamentally an list of `BUMP`s and a list of transactions.
 *
 * A `BUMP` is a partial merkle tree for a 'mined' bitcoin block.
 * It can therefore be used to prove the validity of transaction data
 * for each transaction txid whose merkle path is included in the tree.
 *
 * To be valid, the list of transactions must be sorted in dependency order:
 * oldest transaction first;
 * and each transaction must either
 * have a merkle path in one of the BUMPs, or
 * have all of its input transactions included in the list of transactions.
 *
 * The `Beef` class supports the construction of valid BEEFs by allowing BUMPs
 * (merkle paths) and transactions to be merged sequentially.
 *
 * The `Beef` class also extends the standard by supporting 'known' transactions.
 * A 'known' transaction is represented solely by its txid.
 * To become valid, all the 'known' transactions in a `Beef` must be replaced by full
 * transactions and merkle paths, if they are mined.
 *
 * The purpose of supporting 'known' transactions is that one or both parties
 * generating and exchanging BEEFs often possess partial knowledge of valid transactions
 * due to their history.
 *
 * A valid `Beef` is only required when sent to a party with no shared history,
 * such as a transaction processor.
 *
 * IMPORTANT NOTE:
 * It is fundamental to the BEEF value proposition that only valid transactions and valid
 * merkle path (BUMP) data be added to it. Merging invalid data breaks the `verify` and `isValid`
 * functions. There is no support for removing invalid data. A `Beef` that becomes invalid
 * must be discarded.
 */
export class Beef {
    bumps = [];
    txs = [];
    version = BEEF_V2;
    atomicTxid = undefined;
    txidIndex = undefined;
    txPositionIndex = undefined;
    bumpIndexesByHeight = undefined;
    bumpIndexByTxid = undefined;
    rawBytesCache;
    hexCache;
    atomicBytesCache = new Map();
    atomicCacheTxs;
    atomicCacheBumps;
    atomicCacheVersion;
    rawCacheVersion;
    rawCacheTxs;
    rawCacheBumps;
    bumpState;
    needsSort = true;
    sortResultCache;
    sortTxState;
    constructor(version = BEEF_V2) {
        this.version = version;
    }
    invalidateSerializationCaches() {
        this.rawBytesCache = undefined;
        this.hexCache = undefined;
        this.atomicBytesCache.clear();
        this.atomicCacheTxs = undefined;
        this.atomicCacheBumps = undefined;
        this.atomicCacheVersion = undefined;
        this.rawCacheVersion = undefined;
        this.rawCacheTxs = undefined;
        this.rawCacheBumps = undefined;
    }
    captureSerializationState() {
        this.rawCacheVersion = this.version;
        this.rawCacheTxs = this.captureTransactionState();
        this.rawCacheBumps = Array.from(this.bumps);
        this.captureBumpState();
    }
    captureTransactionState() {
        return this.txs.map(ref => ({
            ref,
            bumpIndex: ref._bumpIndex,
            rawTx: ref._rawTx,
            // Once raw bytes exist, lazily parsing or hashing them does not change
            // their serialized representation and must not evict the forwarding cache.
            tx: ref._rawTx == null ? ref._tx : undefined,
            txid: ref._rawTx == null && ref._tx == null ? ref._txid : undefined
        }));
    }
    transactionStateMatches(cachedTxs) {
        if (cachedTxs?.length !== this.txs.length)
            return false;
        for (let i = 0; i < this.txs.length; i++) {
            const tx = this.txs[i];
            const cached = cachedTxs[i];
            if (cached.ref !== tx ||
                cached.bumpIndex !== tx._bumpIndex ||
                cached.rawTx !== tx._rawTx ||
                cached.tx !== (tx._rawTx == null ? tx._tx : undefined) ||
                cached.txid !== (tx._rawTx == null && tx._tx == null ? tx._txid : undefined))
                return false;
        }
        return true;
    }
    captureBumpState() {
        this.bumpState = this.bumps.map(ref => ({
            ref,
            blockHeight: ref.blockHeight,
            levels: ref.path.map(level => ({
                ref: level,
                leaves: level.map(leaf => ({
                    ref: leaf,
                    offset: leaf.offset,
                    hash: leaf.hash,
                    txid: leaf.txid,
                    duplicate: leaf.duplicate
                }))
            }))
        }));
    }
    bumpLeafStateMatches(leaf, state) {
        return (state.ref === leaf &&
            state.offset === leaf.offset &&
            state.hash === leaf.hash &&
            state.txid === leaf.txid &&
            state.duplicate === leaf.duplicate);
    }
    bumpLevelStateMatches(level, state) {
        return (state.ref === level &&
            state.leaves.length === level.length &&
            level.every((leaf, index) => this.bumpLeafStateMatches(leaf, state.leaves[index])));
    }
    singleBumpStateMatches(bump, state) {
        return (state.ref === bump &&
            state.blockHeight === bump.blockHeight &&
            state.levels.length === bump.path.length &&
            bump.path.every((level, index) => this.bumpLevelStateMatches(level, state.levels[index])));
    }
    bumpStateMatches() {
        if (this.bumpState?.length !== this.bumps.length)
            return false;
        return this.bumps.every((bump, index) => this.singleBumpStateMatches(bump, this.bumpState[index]));
    }
    synchronizeNestedBumpMutations() {
        if (this.bumpState == null) {
            this.captureBumpState();
            return;
        }
        if (!this.bumpStateMatches()) {
            this.invalidateSerializationCaches();
            this.sortResultCache = undefined;
            this.sortTxState = undefined;
            this.needsSort = true;
            this.invalidateBumpIndexes();
            this.captureBumpState();
        }
    }
    serializationCacheMatchesState() {
        if (this.rawBytesCache == null ||
            this.rawCacheVersion !== this.version ||
            !this.transactionStateMatches(this.rawCacheTxs) ||
            this.rawCacheBumps?.length !== this.bumps.length)
            return false;
        for (let i = 0; i < this.bumps.length; i++) {
            if (this.rawCacheBumps[i] !== this.bumps[i])
                return false;
        }
        return true;
    }
    markMutated(requiresSort = true) {
        this.invalidateSerializationCaches();
        this.sortResultCache = undefined;
        this.sortTxState = undefined;
        if (requiresSort) {
            this.needsSort = true;
        }
    }
    ensureSerializableState() {
        for (const tx of this.txs) {
            // Access txid to ensure it is computed before serialization
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            tx.txid;
        }
    }
    synchronizeNestedTransactionMutations() {
        let changed = false;
        for (const tx of this.txs)
            changed = tx.syncRawTxFromTransaction() || changed;
        if (changed) {
            this.invalidateSerializationCaches();
            this.sortResultCache = undefined;
            this.sortTxState = undefined;
            this.needsSort = true;
            this.rebuildTxIndexes();
        }
    }
    ensureSortedForSerialization() {
        if (this.needsSort) {
            this.sortTxs();
        }
    }
    getSerializedBytes() {
        this.synchronizeNestedTransactionMutations();
        this.synchronizeNestedBumpMutations();
        if (this.serializationCacheMatchesState() && this.rawBytesCache != null)
            return this.rawBytesCache;
        this.invalidateSerializationCaches();
        this.ensureSerializableState();
        this.ensureSortedForSerialization();
        const writer = new WriterUint8Array();
        this.toWriter(writer);
        this.rawBytesCache = writer.toUint8Array();
        this.captureSerializationState();
        return this.rawBytesCache;
    }
    getBeefForAtomic(txid) {
        this.synchronizeNestedTransactionMutations();
        this.synchronizeNestedBumpMutations();
        const txidToTx = this.ensureTxidIndex();
        const subject = txidToTx.get(txid);
        if (subject == null) {
            throw new Error(`${txid} does not exist in this Beef`);
        }
        // BRC-95 requires the subject and its complete dependency closure, with no
        // unrelated transactions. Derive that closure from txids rather than array
        // position because parsed BEEF is not guaranteed to arrive pre-sorted.
        const included = this.collectAtomicTransactions(subject, txidToTx);
        const beef = this.copySelectedTransactions(included);
        beef.sortTxs();
        return beef;
    }
    getAtomicSerializedBytes(txid) {
        this.synchronizeNestedTransactionMutations();
        this.synchronizeNestedBumpMutations();
        const cacheMatches = this.atomicCacheVersion === this.version &&
            this.transactionStateMatches(this.atomicCacheTxs) &&
            this.atomicCacheBumps?.length === this.bumps.length &&
            this.bumps.every((bump, index) => this.atomicCacheBumps?.[index] === bump);
        if (!cacheMatches)
            this.atomicBytesCache.clear();
        const cached = this.atomicBytesCache.get(txid);
        if (cached != null)
            return cached;
        const beefBytes = this.getBeefForAtomic(txid).getSerializedBytes();
        const txidBytes = toUint8Array(txid, 'hex');
        const atomic = new Uint8Array(4 + txidBytes.length + beefBytes.length);
        const view = new DataView(atomic.buffer);
        view.setUint32(0, ATOMIC_BEEF, true);
        for (let i = 0; i < txidBytes.length; i++) {
            atomic[4 + i] = txidBytes[txidBytes.length - 1 - i];
        }
        atomic.set(beefBytes, 4 + txidBytes.length);
        this.atomicBytesCache.set(txid, atomic);
        this.atomicCacheTxs = this.captureTransactionState();
        this.atomicCacheBumps = Array.from(this.bumps);
        this.atomicCacheVersion = this.version;
        return atomic;
    }
    collectAtomicTransactions(subject, txidToTx) {
        const included = new Set();
        const stack = [subject];
        while (stack.length > 0) {
            const tx = stack.pop();
            if (tx == null || included.has(tx))
                continue;
            included.add(tx);
            if (this.hasMatchingBump(tx) || tx.isTxidOnly)
                continue;
            for (const inputTxid of tx.inputTxids) {
                const input = txidToTx.get(inputTxid);
                if (input != null)
                    stack.push(input);
            }
        }
        return included;
    }
    hasMatchingBump(tx) {
        const bumpIndex = tx.bumpIndex;
        if (bumpIndex == null ||
            !Number.isSafeInteger(bumpIndex) ||
            bumpIndex < 0 ||
            bumpIndex >= this.bumps.length)
            return false;
        return this.bumps[bumpIndex]?.path[0]?.some(leaf => leaf.hash === tx.txid) ?? false;
    }
    copySelectedTransactions(included) {
        const beef = new Beef(this.version);
        const bumpIndexMap = new Map();
        for (const tx of this.txs) {
            if (!included.has(tx) || !this.hasMatchingBump(tx) || tx.bumpIndex == null)
                continue;
            if (!bumpIndexMap.has(tx.bumpIndex)) {
                bumpIndexMap.set(tx.bumpIndex, beef.bumps.length);
                beef.bumps.push(this.bumps[tx.bumpIndex]);
            }
        }
        for (const tx of this.txs) {
            if (!included.has(tx))
                continue;
            const bumpIndex = tx.bumpIndex == null ? undefined : bumpIndexMap.get(tx.bumpIndex);
            let copy;
            if (tx._rawTx != null) {
                copy = new BeefTx(tx._rawTx, bumpIndex, Array.from(tx.inputTxids));
            }
            else if (tx._tx != null) {
                copy = BeefTx.fromTx(tx._tx, bumpIndex);
            }
            else {
                copy = BeefTx.fromTxid(tx.txid, bumpIndex);
            }
            beef.txs.push(copy);
        }
        return beef;
    }
    /**
     * Checks the BRC-95 transaction-inclusion rule without requiring header-root
     * validation: the subject must exist and every included transaction must be
     * in its recursive dependency graph.
     */
    isAtomic(txid = this.atomicTxid ?? '') {
        this.synchronizeNestedTransactionMutations();
        this.synchronizeNestedBumpMutations();
        if (txid.length === 0)
            return false;
        const txidToTx = this.ensureTxidIndex();
        if (txidToTx.size !== this.txs.length)
            return false;
        const subject = txidToTx.get(txid);
        if (subject == null)
            return false;
        return this.collectAtomicTransactions(subject, txidToTx).size === this.txs.length;
    }
    /**
     * @param txid of `beefTx` to find
     * @returns `BeefTx` in `txs` with `txid`.
     */
    findTxid(txid) {
        this.synchronizeNestedTransactionMutations();
        return this.findTxidIndexed(txid);
    }
    findTxidIndexed(txid) {
        return this.ensureTxidIndex().get(txid);
    }
    ensureTxidIndex() {
        if (this.txidIndex == null || this.txPositionIndex == null)
            this.rebuildTxIndexes();
        return this.txidIndex;
    }
    ensureTxPositionIndex() {
        if (this.txPositionIndex == null || this.txidIndex == null)
            this.rebuildTxIndexes();
        return this.txPositionIndex;
    }
    rebuildTxIndexes() {
        this.txidIndex = new Map();
        this.txPositionIndex = new Map();
        for (let i = 0; i < this.txs.length; i++) {
            const tx = this.txs[i];
            this.txidIndex.set(tx.txid, tx);
            this.txPositionIndex.set(tx.txid, i);
        }
    }
    deleteFromIndex(txid) {
        this.txidIndex?.delete(txid);
        this.txPositionIndex?.delete(txid);
    }
    addToIndex(tx, position = this.txs.length - 1) {
        this.txidIndex?.set(tx.txid, tx);
        this.txPositionIndex?.set(tx.txid, position);
    }
    replaceOrAppendTx(tx) {
        const position = this.ensureTxPositionIndex().get(tx.txid);
        if (position === undefined) {
            this.txs.push(tx);
            this.addToIndex(tx);
        }
        else {
            this.txs[position] = tx;
            this.addToIndex(tx, position);
        }
    }
    /**
     * Replaces `BeefTx` for this txid with txidOnly.
     *
     * Replacement is done so that a `clone()` can be
     * updated by this method without affecting the
     * original.
     *
     * @param txid
     * @returns undefined if txid is unknown.
     */
    makeTxidOnly(txid) {
        const i = this.ensureTxPositionIndex().get(txid);
        if (i === undefined)
            return undefined;
        let btx = this.txs[i];
        if (btx.isTxidOnly) {
            return btx;
        }
        btx = BeefTx.fromTxid(txid);
        this.txs[i] = btx;
        this.addToIndex(btx, i);
        this.tryToValidateBumpIndex(btx);
        this.markMutated(true);
        return btx;
    }
    /**
     * @returns `MerklePath` with level zero hash equal to txid or undefined.
     */
    findBump(txid) {
        this.synchronizeNestedBumpMutations();
        const index = this.ensureBumpTxidIndex().get(txid);
        return index === undefined ? undefined : this.bumps[index];
    }
    ensureBumpTxidIndex() {
        if (this.bumpIndexByTxid == null) {
            this.bumpIndexByTxid = new Map();
            for (let i = 0; i < this.bumps.length; i++) {
                for (const leaf of this.bumps[i].path[0]) {
                    if (typeof leaf.hash === 'string')
                        this.bumpIndexByTxid.set(leaf.hash, i);
                }
            }
        }
        return this.bumpIndexByTxid;
    }
    ensureBumpHeightIndex() {
        if (this.bumpIndexesByHeight == null) {
            this.bumpIndexesByHeight = new Map();
            for (let i = 0; i < this.bumps.length; i++) {
                const bump = this.bumps[i];
                const indexes = this.bumpIndexesByHeight.get(bump.blockHeight) ?? [];
                indexes.push(i);
                this.bumpIndexesByHeight.set(bump.blockHeight, indexes);
            }
        }
        return this.bumpIndexesByHeight;
    }
    invalidateBumpIndexes() {
        this.bumpIndexesByHeight = undefined;
        this.bumpIndexByTxid = undefined;
    }
    /**
     * Finds a Transaction in this `Beef`
     * and adds any missing input SourceTransactions from this `Beef`.
     *
     * The result is suitable for signing.
     *
     * @param txid The id of the target transaction.
     * @returns Transaction with all available input `SourceTransaction`s from this Beef.
     */
    findTransactionForSigning(txid) {
        const beefTx = this.findTxid(txid);
        if (beefTx?.tx == null)
            return undefined; // Ensure beefTx.tx exists before using it
        for (const i of beefTx.tx.inputs) {
            if (i.sourceTransaction == null) {
                const itx = this.findTxidIndexed(verifyNotNull(i.sourceTXID, 'sourceTXID must be valid'));
                if (itx != null) {
                    i.sourceTransaction = itx.tx;
                }
            }
        }
        return beefTx.tx;
    }
    /**
     * Builds the proof tree rooted at a specific `Transaction`.
     *
     * To succeed, the Beef must contain all the required transaction and merkle path data.
     *
     * @param txid The id of the target transaction.
     * @returns Transaction with input `SourceTransaction` and `MerklePath` populated from this Beef.
     */
    findAtomicTransaction(txid) {
        const beefTx = this.findTxid(txid);
        if (beefTx?.tx == null)
            return undefined; // Ensure beefTx.tx exists before using it
        this.addInputProof(beefTx.tx);
        return beefTx.tx;
    }
    /** Iteratively attach merkle paths and source transactions to all inputs. */
    addInputProof(tx) {
        const visited = new Set();
        const stack = [tx];
        while (stack.length > 0) {
            const current = stack.pop();
            if (current == null)
                continue;
            const txid = current.id('hex');
            if (visited.has(txid))
                continue;
            visited.add(txid);
            const mp = this.findBump(txid);
            if (mp != null) {
                current.merklePath = mp;
                continue;
            }
            for (const input of current.inputs) {
                this.resolveInputSource(input);
                if (input.sourceTransaction != null)
                    stack.push(input.sourceTransaction);
            }
        }
    }
    resolveInputSource(i) {
        if (i.sourceTransaction == null) {
            // findAtomicTransaction() synchronized every nested transaction before
            // entering this traversal; repeating that O(V) pass for every edge would
            // turn proof linking back into O(VE).
            const itx = this.findTxidIndexed(verifyNotNull(i.sourceTXID, 'sourceTXID must be valid'));
            if (itx != null) {
                i.sourceTransaction = itx.tx;
            }
        }
    }
    /**
     * Merge a MerklePath that is assumed to be fully valid.
     * @param bump
     * @returns index of merged bump
     */
    mergeBump(bump) {
        this.synchronizeNestedTransactionMutations();
        this.synchronizeNestedBumpMutations();
        this.markMutated(false);
        return this.mergeBumpEntry(bump);
    }
    /**
     * Merge several independently proven transactions in one mutation pass.
     *
     * This is equivalent to calling `mergeRawTx` followed by `mergeBump` for
     * every entry, but synchronizes nested BEEF state only once. That distinction
     * matters for wallets assembling a BEEF from a fragmented UTXO set because
     * proof paths are otherwise re-scanned after every input.
     */
    mergeProvenTxs(entries) {
        if (entries.length === 0)
            return [];
        this.synchronizeNestedTransactionMutations();
        this.synchronizeNestedBumpMutations();
        this.markMutated(true);
        const merged = [];
        for (const entry of entries) {
            merged.push(this.mergeRawTxEntry(entry.rawTx));
        }
        // A real wallet often owns many transactions mined in the same block.
        // Combine those paths once per block root, rather than repeatedly hashing,
        // combining, and trimming an ever-growing compound proof.
        const heightCounts = new Map();
        for (const entry of entries) {
            const height = entry.merklePath.blockHeight;
            heightCounts.set(height, (heightCounts.get(height) ?? 0) + 1);
        }
        const groups = new Map();
        for (let index = 0; index < entries.length; index++) {
            const path = entries[index].merklePath;
            const rootHint = entries[index].merkleRoot;
            const key = heightCounts.get(path.blockHeight) === 1
                ? `${path.blockHeight}:single:${index}`
                : `${path.blockHeight}:${rootHint ?? path.computeRoot()}`;
            const group = groups.get(key);
            if (group == null) {
                groups.set(key, { first: index, paths: [path], validateCombined: rootHint != null });
            }
            else {
                group.paths.push(path);
                group.validateCombined = group.validateCombined || rootHint != null;
            }
        }
        for (const group of [...groups.values()].sort((a, b) => a.first - b.first)) {
            this.mergeBumpEntry(this.combineCompatibleBumps(group.paths, group.validateCombined));
        }
        return merged;
    }
    /** Combine already root-matched paths while preserving the first path reference. */
    combineCompatibleBumps(paths, validateCombined = false) {
        const combined = paths[0];
        if (paths.length === 1 && !validateCombined)
            return combined;
        const levels = combined.path.map(level => new Map(level.map(leaf => [leaf.offset, leaf])));
        for (let pathIndex = 1; pathIndex < paths.length; pathIndex++) {
            mergeCompatibleBumpLevels(levels, paths[pathIndex], combined.path.length, validateCombined);
        }
        const combinedPath = levels.map(level => [...level.values()]);
        if (validateCombined) {
            // Storage-backed paths may have intentionally deferred root validation.
            // Validate the compound path once here so shared branches are hashed
            // once, while corrupt or mismatched proofs still fail closed.
            const validated = new MerklePath(combined.blockHeight, combinedPath);
            validated.trim();
            return validated;
        }
        combined.path = combinedPath;
        combined.trim();
        return combined;
    }
    /** Merge one bump after the caller has synchronized and marked the BEEF. */
    mergeBumpEntry(bump) {
        const bumpIndex = this.findOrInsertBump(bump);
        const b = this.bumps[bumpIndex];
        const txIndex = this.ensureTxidIndex();
        for (const leaf of b.path[0]) {
            if (typeof leaf.hash !== 'string')
                continue;
            const tx = txIndex.get(leaf.hash);
            if (tx != null && tx.bumpIndex == null)
                this.tryMarkTxProvenByBump(tx, b, bumpIndex);
        }
        return bumpIndex;
    }
    /**
     * Find an existing compatible bump or insert a new one; return its index.
     */
    findOrInsertBump(bump) {
        const byHeight = this.ensureBumpHeightIndex();
        const byTxid = this.ensureBumpTxidIndex();
        const sameHeight = byHeight.get(bump.blockHeight) ?? [];
        if (sameHeight.length > 0) {
            const root = bump.computeRoot();
            for (const existing of sameHeight) {
                if (this.bumps[existing].computeRoot() !== root)
                    continue;
                this.bumps[existing].combine(bump);
                indexBumpTxids(this.bumps[existing], existing, byTxid);
                return existing;
            }
        }
        this.bumps.push(bump);
        const index = this.bumps.length - 1;
        sameHeight.push(index);
        byHeight.set(bump.blockHeight, sameHeight);
        indexBumpTxids(bump, index, byTxid);
        return index;
    }
    /** If bump's level-0 path contains tx's txid, record the bumpIndex on tx. */
    tryMarkTxProvenByBump(tx, b, bumpIndex) {
        const txid = tx.txid;
        for (const n of b.path[0]) {
            if (n.hash === txid) {
                tx.bumpIndex = bumpIndex;
                n.txid = true;
                break;
            }
        }
    }
    /**
     * Merge a serialized transaction.
     *
     * Checks that a transaction with the same txid hasn't already been merged.
     *
     * Replaces existing transaction with same txid.
     *
     * @param rawTx
     * @param bumpIndex Optional. If a number, must be valid index into bumps array.
     * @returns txid of rawTx
     */
    mergeRawTx(rawTx, bumpIndex) {
        this.synchronizeNestedTransactionMutations();
        this.markMutated(true);
        return this.mergeRawTxEntry(rawTx, bumpIndex);
    }
    /** Merge one raw transaction after the caller has synchronized and marked the BEEF. */
    mergeRawTxEntry(rawTx, bumpIndex) {
        const newTx = new BeefTx(rawTx, bumpIndex);
        this.replaceOrAppendTx(newTx);
        this.tryToValidateBumpIndex(newTx);
        return newTx;
    }
    mergeTransactionEntry(current) {
        const bumpIndex = current.merklePath == null ? undefined : this.mergeBumpEntry(current.merklePath);
        const newTx = new BeefTx(current, bumpIndex);
        this.replaceOrAppendTx(newTx);
        this.tryToValidateBumpIndex(newTx);
        return newTx;
    }
    queueSourceTransactions(current, stack) {
        for (let i = current.inputs.length - 1; i >= 0; i--) {
            const source = current.inputs[i].sourceTransaction;
            if (source != null)
                stack.push(source);
        }
    }
    /**
     * Merge a `Transaction` and any referenced `merklePath` and `sourceTransaction`, recursifely.
     *
     * Replaces existing transaction with same txid.
     *
     * Attempts to match an existing bump to the new transaction.
     *
     * @param tx
     * @returns txid of tx
     */
    mergeTransaction(tx) {
        this.synchronizeNestedTransactionMutations();
        this.markMutated(true);
        return this.mergeTransactionGraph(tx);
    }
    /** Merge one transaction graph after the caller has synchronized and marked the BEEF. */
    mergeTransactionGraph(tx) {
        tx.materializeSourceTXIDs();
        const rootTxid = tx.id('hex');
        const visited = new Set();
        const stack = [tx];
        let root;
        while (stack.length > 0) {
            const current = stack.pop();
            if (current == null)
                continue;
            const txid = current.id('hex');
            if (visited.has(txid))
                continue;
            visited.add(txid);
            const newTx = this.mergeTransactionEntry(current);
            if (txid === rootTxid)
                root = newTx;
            if (newTx.bumpIndex === undefined)
                this.queueSourceTransactions(current, stack);
        }
        if (root == null)
            throw new Error('Failed to merge root transaction');
        return root;
    }
    /**
     * Removes an existing transaction from the BEEF, given its TXID
     * @param txid TXID of the transaction to remove
     */
    removeExistingTxid(txid) {
        const existingTxIndex = this.ensureTxPositionIndex().get(txid);
        if (existingTxIndex !== undefined) {
            // This public method historically preserved the relative order of all
            // remaining entries. Keep that compatibility guarantee; hot merge paths
            // use replaceOrAppendTx and do not pay for this stable removal.
            this.txs.splice(existingTxIndex, 1);
            this.rebuildTxIndexes();
            this.markMutated(true);
        }
    }
    mergeTxidOnly(txid) {
        let tx = this.findTxid(txid);
        if (tx == null) {
            tx = new BeefTx(txid);
            this.txs.push(tx);
            this.addToIndex(tx);
            this.tryToValidateBumpIndex(tx);
            this.markMutated(true);
        }
        return tx;
    }
    mergeBeefTx(btx) {
        let beefTx = this.findTxid(btx.txid);
        if (btx.isTxidOnly && beefTx == null) {
            beefTx = this.mergeTxidOnly(btx.txid);
        }
        else if (btx._tx != null && (beefTx == null || beefTx.isTxidOnly)) {
            beefTx = this.mergeTransaction(btx._tx);
        }
        else if (btx._rawTx != null && (beefTx == null || beefTx.isTxidOnly)) {
            beefTx = this.mergeRawTx(btx._rawTx);
        }
        if (beefTx == null) {
            throw new Error(`Failed to merge BeefTx for txid: ${btx.txid}`);
        }
        return beefTx;
    }
    /** Merge one BEEF transaction after the caller has synchronized and marked the BEEF. */
    mergeBeefTxEntry(btx) {
        let beefTx = this.findTxidIndexed(btx.txid);
        if (btx.isTxidOnly && beefTx == null) {
            beefTx = BeefTx.fromTxid(btx.txid);
            this.txs.push(beefTx);
            this.addToIndex(beefTx);
            this.tryToValidateBumpIndex(beefTx);
        }
        else if (btx._tx != null && (beefTx == null || beefTx.isTxidOnly)) {
            beefTx = this.mergeTransactionGraph(btx._tx);
        }
        else if (btx._rawTx != null && (beefTx == null || beefTx.isTxidOnly)) {
            beefTx = this.mergeRawTxEntry(btx._rawTx);
        }
        if (beefTx == null) {
            throw new Error(`Failed to merge BeefTx for txid: ${btx.txid}`);
        }
        return beefTx;
    }
    mergeBeef(beef) {
        const b = beef instanceof Beef ? beef : Beef.fromBinary(beef);
        this.synchronizeNestedTransactionMutations();
        this.synchronizeNestedBumpMutations();
        this.markMutated(true);
        for (const bump of b.bumps) {
            this.mergeBumpEntry(bump);
        }
        for (const tx of b.txs) {
            this.mergeBeefTxEntry(tx);
        }
    }
    /**
     * Sorts `txs` and checks structural validity of beef.
     *
     * Does NOT verify merkle roots.
     *
     * Validity requirements:
     * 1. No 'known' txids, unless `allowTxidOnly` is true.
     * 2. All transactions have bumps or their inputs chain back to bumps (or are known).
     * 3. Order of transactions satisfies dependencies before dependents.
     * 4. No transactions with duplicate txids.
     *
     * @param allowTxidOnly optional. If true, transaction txid only is assumed valid
     */
    isValid(allowTxidOnly) {
        return this.verifyValid(allowTxidOnly).valid;
    }
    /**
     * Sorts `txs` and confirms validity of transaction data contained in beef
     * by validating structure of this beef and confirming computed merkle roots
     * using `chainTracker`.
     *
     * Validity requirements:
     * 1. No 'known' txids, unless `allowTxidOnly` is true.
     * 2. All transactions have bumps or their inputs chain back to bumps (or are known).
     * 3. Order of transactions satisfies dependencies before dependents.
     * 4. No transactions with duplicate txids.
     *
     * @param chainTracker Used to verify computed merkle path roots for all bump txids.
     * @param allowTxidOnly optional. If true, transaction txid is assumed valid
     */
    async verify(chainTracker, allowTxidOnly) {
        const r = this.verifyValid(allowTxidOnly);
        if (!r.valid)
            return false;
        for (const height of Object.keys(r.roots)) {
            const isValid = await chainTracker.isValidRootForHeight(r.roots[height], Number(height));
            if (!isValid) {
                return false;
            }
        }
        return true;
    }
    /**
     * Sorts `txs` and confirms validity of transaction data contained in beef
     * by validating structure of this beef.
     *
     * Returns block heights and merkle root values to be confirmed by a chaintracker.
     *
     * Validity requirements:
     * 1. No 'known' txids, unless `allowTxidOnly` is true.
     * 2. All transactions have bumps or their inputs chain back to bumps (or are known).
     * 3. Order of transactions satisfies dependencies before dependents.
     * 4. No transactions with duplicate txids.
     *
     * @param allowTxidOnly optional. If true, transaction txid is assumed valid
     * @returns {{valid: boolean, roots: Record<number, string>}}
     * `valid` is true iff this Beef is structuraly valid.
     * `roots` is a record where keys are block heights and values are the corresponding merkle roots to be validated.
     */
    verifyValid(allowTxidOnly) {
        this.synchronizeNestedBumpMutations();
        const r = {
            valid: false,
            roots: {}
        };
        if (this.atomicTxid != null && !this.isAtomic(this.atomicTxid))
            return r;
        const sr = this.sortTxs();
        if (this.hasDuplicateTxids())
            return r;
        if (sr.missingInputs.length > 0 ||
            sr.notValid.length > 0 ||
            (sr.txidOnly.length > 0 && allowTxidOnly !== true) ||
            sr.withMissingInputs.length > 0) {
            return r;
        }
        // valid txids: only txids if allowed, bump txids, then txids with input's in txids
        const txids = {};
        if (!this.collectTxidOnlyTxids(txids, allowTxidOnly))
            return r;
        if (!this.collectBumpTxids(txids, r))
            return r;
        if (!this.verifyBumpIndexLeaves())
            return r;
        if (!this.verifyInputDependencies(txids))
            return r;
        r.valid = true;
        return r;
    }
    hasDuplicateTxids() {
        const seen = new Set();
        for (const tx of this.txs) {
            if (seen.has(tx.txid))
                return true;
            seen.add(tx.txid);
        }
        return false;
    }
    /** Add txidOnly transaction txids; return false if not allowed. */
    collectTxidOnlyTxids(txids, allowTxidOnly) {
        for (const tx of this.txs) {
            if (!tx.isTxidOnly)
                continue;
            if (allowTxidOnly !== true)
                return false;
            txids[tx.txid] = true;
        }
        return true;
    }
    /**
     * Record txids proven by bumps; validate all bump roots agree per block height.
     * Returns false if any root conflict is detected.
     */
    collectBumpTxids(txids, r) {
        for (const b of this.bumps) {
            for (const n of b.path[0]) {
                if (n.txid !== true || typeof n.hash !== 'string' || n.hash.length === 0)
                    continue;
                txids[n.hash] = true;
                if (!this.confirmComputedRoot(b, n.hash, r))
                    return false;
            }
        }
        return true;
    }
    /** Verify that every tx with a bumpIndex has a matching txid leaf in its bump. */
    verifyBumpIndexLeaves() {
        for (const t of this.txs) {
            if (t.bumpIndex === undefined)
                continue;
            if (!Number.isSafeInteger(t.bumpIndex) || t.bumpIndex < 0 || t.bumpIndex >= this.bumps.length)
                return false;
            const leaf = this.bumps[t.bumpIndex]?.path[0]?.find(l => l.hash === t.txid);
            if (leaf == null)
                return false;
        }
        return true;
    }
    /** Verify all input txids appear before the spending tx in sorted order. */
    verifyInputDependencies(txids) {
        for (const t of this.txs) {
            for (const i of t.inputTxids) {
                if (!txids[i])
                    return false;
            }
            txids[t.txid] = true;
        }
        return true;
    }
    /** Confirm the computed merkle root for txid in bump matches previously accepted root for that height. */
    confirmComputedRoot(b, txid, r) {
        const root = b.computeRoot(txid);
        if (r.roots[b.blockHeight] === undefined || r.roots[b.blockHeight] === '') {
            // accept the root as valid for this block and reuse for subsequent txids
            r.roots[b.blockHeight] = root;
        }
        return r.roots[b.blockHeight] === root;
    }
    /**
     * Serializes this data to `writer`
     * @param writer
     */
    toWriter(writer) {
        writer.writeUInt32LE(this.version);
        writer.writeVarIntNum(this.bumps.length);
        for (const b of this.bumps) {
            writer.write(writer instanceof WriterUint8Array ? b.toBinaryUint8Array() : b.toBinary());
        }
        writer.writeVarIntNum(this.txs.length);
        for (const tx of this.txs) {
            tx.toWriter(writer, this.version);
        }
    }
    /**
     * Returns a binary array representing the serialized BEEF
     * @returns A binary array representing the BEEF
     * @returns An array of byte values containing binary serialization of the BEEF
     */
    toBinary() {
        return Array.from(this.getSerializedBytes());
    }
    /**
     * Returns a binary array representing the serialized BEEF
     * @returns A Uint8Array containing binary serialization of the BEEF
     */
    toUint8Array() {
        return this.getSerializedBytes();
    }
    /**
     * Serialize this Beef as AtomicBEEF.
     *
     * `txid` must exist
     *
     * includes exactly the subject transaction and its recursive dependencies
     *
     * @param txid
     * @returns serialized contents of this Beef with AtomicBEEF prefix.
     */
    toBinaryAtomic(txid) {
        return Array.from(this.getAtomicSerializedBytes(txid));
    }
    /**
     * Serialize this Beef as AtomicBEEF.
     *
     * `txid` must exist
     *
     * includes exactly the subject transaction and its recursive dependencies
     *
     * @param txid
     * @returns serialized contents of this Beef with AtomicBEEF prefix.
     */
    toUint8ArrayAtomic(txid) {
        return this.getAtomicSerializedBytes(txid);
    }
    /**
     * Returns a hex string representing the serialized BEEF
     * @returns A hex string representing the BEEF
     */
    toHex() {
        const bytes = this.getSerializedBytes();
        if (this.hexCache != null)
            return this.hexCache;
        const hex = toHex(bytes);
        this.hexCache = hex;
        return hex;
    }
    static fromReader(br) {
        const serializedStart = br.pos;
        let version = br.readUInt32LE();
        let atomicTxid;
        let beefStart = serializedStart;
        if (version === ATOMIC_BEEF) {
            // Skip the txid and re-read the BEEF version
            atomicTxid = toHex(br.readReverse(32));
            beefStart = br.pos;
            version = br.readUInt32LE();
        }
        if (version !== BEEF_V1 && version !== BEEF_V2) {
            throw new Error(`Serialized BEEF must start with ${BEEF_V1} or ${BEEF_V2} but starts with ${version}`);
        }
        const beef = new Beef(version);
        const bumpsLength = br.readVarIntNum();
        for (let i = 0; i < bumpsLength; i++) {
            const bump = MerklePath.fromReader(br, false);
            beef.bumps.push(bump);
        }
        const txsLength = br.readVarIntNum();
        for (let i = 0; i < txsLength; i++) {
            const beefTx = BeefTx.fromReader(br, version);
            beef.txs.push(beefTx);
        }
        beef.atomicTxid = atomicTxid;
        if (br instanceof ReaderUint8Array) {
            beef.rawBytesCache = br.bin.subarray(beefStart, br.pos);
            beef.captureSerializationState();
        }
        return beef;
    }
    /**
     * Constructs an instance of the Beef class based on the provided binary array
     * @param bin The binary array or Uint8Array from which to construct BEEF
     * @returns An instance of the Beef class constructed from the binary data
     */
    static fromBinary(bin) {
        // Copy for isolation while preserving the historical prefix-parser
        // behavior. The explicit View API below performs strict framing checks.
        return Beef.fromReader(new ReaderUint8Array(Uint8Array.from(bin)));
    }
    /**
     * Parses BEEF while retaining zero-copy views over `bin`. The caller must not
     * mutate the buffer for the lifetime of the returned object.
     */
    static fromBinaryView(bin) {
        const br = new ReaderUint8Array(bin);
        const beef = Beef.fromReader(br);
        if (!br.eof())
            throw new Error('Serialized BEEF contains trailing data');
        return beef;
    }
    /**
     * Constructs an instance of the Beef class based on the provided string
     * @param s The string value from which to construct BEEF
     * @param enc The encoding of the string value from which BEEF should be constructed
     * @returns An instance of the Beef class constructed from the string
     */
    static fromString(s, enc = 'hex') {
        const bin = toUint8Array(s, enc);
        const br = new ReaderUint8Array(bin);
        return Beef.fromReader(br);
    }
    /**
     * Try to validate newTx.bumpIndex by looking for an existing bump
     * that proves newTx.txid
     *
     * @param newTx A new `BeefTx` that has been added to this.txs
     * @returns true if a bump was found, false otherwise
     */
    tryToValidateBumpIndex(newTx) {
        if (newTx.bumpIndex !== undefined) {
            return true;
        }
        const txid = newTx.txid;
        const i = this.ensureBumpTxidIndex().get(txid);
        if (i === undefined)
            return false;
        newTx.bumpIndex = i;
        const leaf = this.bumps[i].path[0].find(b => b.hash === txid);
        if (leaf != null)
            leaf.txid = true;
        return true;
    }
    /**
     * Sort the `txs` by input txid dependency order:
     * - Oldest Tx Anchored by Path or txid only
     * - Newer Txs depending on Older parents
     * - Newest Tx
     *
     * with proof (MerklePath) last, longest chain of dependencies first
     *
     * @returns `{ missingInputs, notValid, valid, withMissingInputs }`
     */
    sortTxs() {
        this.synchronizeNestedTransactionMutations();
        this.synchronizeNestedBumpMutations();
        if (this.sortResultCache != null && this.sortTxStateMatches()) {
            return this.cloneSortResult(this.sortResultCache);
        }
        this.sortResultCache = undefined;
        this.sortTxState = undefined;
        // Hashtable of valid txids (with proof or all inputs chain to proof)
        const validTxids = {};
        // Hashtable of all transaction txids to transaction
        const txidToTx = {};
        // sorted transactions: hasProof to with longest dependency chain
        const result = [];
        const txidOnly = [];
        // Partition into proven, txidOnly, and remaining queue
        let queue = this.partitionTxs(txidToTx, validTxids, result, txidOnly);
        // Separate queue entries that have missing inputs
        const { txsMissingInputs, missingInputs, remaining } = this.separateMissingInputs(queue, txidToTx);
        queue = remaining;
        // Topological sort of remaining queue
        const txsNotValid = this.topoSort(queue, validTxids, result);
        // New order of txs is unsortable (missing inputs or depends on missing inputs), txidOnly, sorted (so newest sorted is last)
        this.txs = txsMissingInputs.concat(txsNotValid).concat(txidOnly).concat(result);
        this.needsSort = false;
        this.invalidateSerializationCaches();
        this.rebuildTxIndexes();
        const sortResult = {
            missingInputs: Object.keys(missingInputs),
            notValid: txsNotValid.map(tx => tx.txid),
            valid: Object.keys(validTxids),
            withMissingInputs: txsMissingInputs.map(tx => tx.txid),
            txidOnly: txidOnly.map(tx => tx.txid)
        };
        this.sortResultCache = sortResult;
        this.captureSortTxState();
        return this.cloneSortResult(sortResult);
    }
    captureSortTxState() {
        this.sortTxState = this.txs.map(tx => ({
            ref: tx,
            txid: tx.txid,
            bumpIndex: tx.bumpIndex,
            isTxidOnly: tx.isTxidOnly,
            inputTxids: [...tx.inputTxids]
        }));
    }
    sortTxStateMatches() {
        if (this.sortTxState?.length !== this.txs.length)
            return false;
        for (let index = 0; index < this.txs.length; index++) {
            const tx = this.txs[index];
            const state = this.sortTxState[index];
            if (state.ref !== tx ||
                state.txid !== tx.txid ||
                state.bumpIndex !== tx.bumpIndex ||
                state.isTxidOnly !== tx.isTxidOnly ||
                state.inputTxids.length !== tx.inputTxids.length)
                return false;
            for (let inputIndex = 0; inputIndex < tx.inputTxids.length; inputIndex++) {
                if (state.inputTxids[inputIndex] !== tx.inputTxids[inputIndex])
                    return false;
            }
        }
        return true;
    }
    cloneSortResult(result) {
        return {
            missingInputs: [...result.missingInputs],
            notValid: [...result.notValid],
            valid: [...result.valid],
            withMissingInputs: [...result.withMissingInputs],
            txidOnly: [...result.txidOnly]
        };
    }
    /**
     * Partition txs into proven (result), txidOnly, and a queue of the rest.
     * Populates txidToTx and validTxids as side-effects.
     */
    partitionTxs(txidToTx, validTxids, result, txidOnly) {
        const queue = [];
        for (const tx of this.txs) {
            txidToTx[tx.txid] = tx;
            tx.isValid = tx.hasProof;
            if (tx.isValid) {
                validTxids[tx.txid] = true;
                result.push(tx);
            }
            else if (tx.isTxidOnly && tx.inputTxids.length === 0) {
                validTxids[tx.txid] = true;
                txidOnly.push(tx);
            }
            else {
                queue.push(tx);
            }
        }
        return queue;
    }
    /**
     * Separate queue entries that have at least one input txid not present in txidToTx.
     */
    separateMissingInputs(candidates, txidToTx) {
        const missingInputs = {};
        const txsMissingInputs = [];
        const remaining = [];
        for (const tx of candidates) {
            let hasMissingInput = false;
            for (const inputTxid of tx.inputTxids) {
                if (txidToTx[inputTxid] === undefined) {
                    missingInputs[inputTxid] = true;
                    hasMissingInput = true;
                }
            }
            if (hasMissingInput) {
                txsMissingInputs.push(tx);
            }
            else {
                remaining.push(tx);
            }
        }
        return { txsMissingInputs, missingInputs, remaining };
    }
    /**
     * Topologically sort queue into result; return anything that cannot be sorted.
     */
    topoSort(queue, validTxids, result) {
        const { indegree, dependents, originalIndex, round } = this.buildTopoSortGraph(queue, validTxids);
        const processed = this.processTopoSortQueue(queue, indegree, dependents, originalIndex, round);
        this.appendTopoSortResult(queue, processed, round, validTxids, result);
        return queue.filter(tx => !processed.has(tx.txid));
    }
    buildTopoSortGraph(queue, validTxids) {
        const candidates = new Set(queue.map(tx => tx.txid));
        const indegree = new Map();
        const dependents = new Map();
        const originalIndex = new Map(queue.map((tx, index) => [tx.txid, index]));
        const round = new Map();
        for (const tx of queue) {
            let degree = 0;
            for (const inputTxid of tx.inputTxids) {
                if (validTxids[inputTxid])
                    continue;
                degree++;
                if (candidates.has(inputTxid)) {
                    const children = dependents.get(inputTxid) ?? [];
                    children.push(tx);
                    dependents.set(inputTxid, children);
                }
            }
            indegree.set(tx.txid, degree);
            round.set(tx.txid, 0);
        }
        return { indegree, dependents, originalIndex, round };
    }
    processTopoSortQueue(queue, indegree, dependents, originalIndex, round) {
        const ready = queue.filter(tx => indegree.get(tx.txid) === 0);
        const processed = new Set();
        for (const tx of ready) {
            if (processed.has(tx.txid))
                continue;
            processed.add(tx.txid);
            for (const dependent of dependents.get(tx.txid) ?? []) {
                const nextRound = (round.get(tx.txid) ?? 0) +
                    ((originalIndex.get(tx.txid) ?? 0) > (originalIndex.get(dependent.txid) ?? 0) ? 1 : 0);
                round.set(dependent.txid, Math.max(round.get(dependent.txid) ?? 0, nextRound));
                const next = (indegree.get(dependent.txid) ?? 0) - 1;
                indegree.set(dependent.txid, next);
                if (next === 0)
                    ready.push(dependent);
            }
        }
        return processed;
    }
    appendTopoSortResult(queue, processed, round, validTxids, result) {
        // Preserve the legacy repeated-scan ordering without repeating scans: a
        // dependency that originally appears after its child advances that child
        // to the next scan round. Bucketing by round and original position is O(V).
        const byRound = [];
        for (const tx of queue) {
            if (!processed.has(tx.txid))
                continue;
            const txRound = round.get(tx.txid) ?? 0;
            const bucket = byRound[txRound] ?? [];
            bucket.push(tx);
            byRound[txRound] = bucket;
        }
        for (const bucket of byRound) {
            for (const tx of bucket ?? []) {
                validTxids[tx.txid] = true;
                result.push(tx);
            }
        }
    }
    /**
     * @returns a shallow copy of this beef
     */
    clone() {
        const c = new Beef();
        c.version = this.version;
        c.bumps = Array.from(this.bumps);
        c.txs = Array.from(this.txs);
        c.txidIndex = undefined;
        c.txPositionIndex = undefined;
        c.bumpIndexesByHeight = undefined;
        c.bumpIndexByTxid = undefined;
        c.needsSort = this.needsSort;
        c.sortResultCache = this.sortResultCache == null ? undefined : this.cloneSortResult(this.sortResultCache);
        if (c.sortResultCache != null)
            c.captureSortTxState();
        c.hexCache = this.hexCache;
        c.rawBytesCache = this.rawBytesCache;
        if (c.rawBytesCache != null)
            c.captureSerializationState();
        return c;
    }
    /**
     * Ensure that all the txids in `knownTxids` are txidOnly
     * @param knownTxids
     */
    trimKnownTxids(knownTxids) {
        let mutated = this.removeKnownTxidOnlyTxs(new Set(knownTxids));
        mutated = this.reindexBumps() || mutated;
        if (mutated) {
            this.markMutated(true);
        }
    }
    /** Remove txidOnly entries that appear in knownTxids; return true if any were removed. */
    removeKnownTxidOnlyTxs(knownTxids) {
        const originalLength = this.txs.length;
        this.txs = this.txs.filter(tx => !(tx.isTxidOnly && knownTxids.has(tx.txid)));
        const mutated = this.txs.length !== originalLength;
        if (mutated)
            this.rebuildTxIndexes();
        return mutated;
    }
    /**
     * Remove bumps that are no longer referenced by any tx and update bumpIndex references.
     * Returns true if any bumps were removed.
     */
    reindexBumps() {
        const referencedBumpIndices = new Set();
        for (const tx of this.txs) {
            if (tx.bumpIndex !== undefined) {
                referencedBumpIndices.add(tx.bumpIndex);
            }
        }
        if (referencedBumpIndices.size >= this.bumps.length)
            return false;
        // Build mapping of old indices to new indices after removal
        const indexMap = new Map();
        let newIndex = 0;
        for (let i = 0; i < this.bumps.length; i++) {
            if (referencedBumpIndices.has(i)) {
                indexMap.set(i, newIndex);
                newIndex++;
            }
        }
        // Remove unreferenced bumps
        this.bumps = this.bumps.filter((_, i) => referencedBumpIndices.has(i));
        // Update all transaction bumpIndex references
        for (const tx of this.txs) {
            if (tx.bumpIndex === undefined)
                continue;
            const mapped = indexMap.get(tx.bumpIndex);
            if (mapped === undefined) {
                throw new Error(`Internal error: bumpIndex ${tx.bumpIndex} not found in indexMap`);
            }
            tx.bumpIndex = mapped;
        }
        this.invalidateBumpIndexes();
        return true;
    }
    /**
     * @returns array of transaction txids that either have a proof or whose inputs chain back to a proven transaction.
     */
    getValidTxids() {
        const r = this.sortTxs();
        return r.valid;
    }
    /**
     * @returns Summary of `Beef` contents as multi-line string.
     */
    toLogString() {
        let log = '';
        log += `BEEF with ${this.bumps.length} BUMPS and ${this.txs.length} Transactions, isValid ${this.isValid().toString()}\n`;
        let i = -1;
        for (const b of this.bumps) {
            i++;
            log += `  BUMP ${i}\n    block: ${b.blockHeight}\n    txids: [\n${b.path[0]
                .filter(n => n.txid === true)
                .map(n => `      '${n.hash ?? ''}'`)
                .join(',\n')}\n    ]\n`;
        }
        i = -1;
        for (const t of this.txs) {
            i++;
            log += `  TX ${i}\n    txid: ${t.txid}\n`;
            if (t.bumpIndex !== undefined) {
                log += `    bumpIndex: ${t.bumpIndex}\n`;
            }
            if (t.isTxidOnly) {
                log += '    txidOnly\n';
            }
            else {
                log += `    rawTx length=${t.rawTx?.length ?? 0}\n`;
            }
            if (t.inputTxids.length > 0) {
                const inputLines = t.inputTxids.map(it => `      '${it}'`).join(',\n');
                log += `    inputs: [\n${inputLines}\n    ]\n`;
            }
        }
        return log;
    }
    /**
     * In some circumstances it may be helpful for the BUMP MerklePaths to include
     * leaves that can be computed from row zero.
     */
    addComputedLeaves() {
        for (const bump of this.bumps) {
            for (let row = 1; row < bump.path.length; row++) {
                this.addComputedLeavesForRow(bump, row);
            }
        }
    }
    /** Add any missing computable leaf at `row` derived from two known leaves at `row - 1`. */
    addComputedLeavesForRow(bump, row) {
        const hashPair = (m) => toHex(hash256(toArray(m, 'hex').reverse()).reverse());
        for (const leafL of bump.path[row - 1]) {
            if (typeof leafL.hash !== 'string' || (leafL.offset & 1) !== 0)
                continue;
            const leafR = bump.path[row - 1].find(l => l.offset === leafL.offset + 1);
            if (leafR === undefined || typeof leafR.hash !== 'string')
                continue;
            const offsetOnRow = leafL.offset >> 1;
            if (bump.path[row].every(l => l.offset !== offsetOnRow)) {
                // Computable leaf is missing... add it.
                bump.path[row].push({
                    offset: offsetOnRow,
                    // String concatenation puts the right leaf on the left of the left leaf hash
                    hash: hashPair(leafR.hash + leafL.hash)
                });
            }
        }
    }
}
export default Beef;
//# sourceMappingURL=Beef.js.map